import Button from "@components/Button/Button";
import { ButtonUpdated } from "@components/ButtonUpdated/ButtonUpdated";
import Modal from "@components/Modal/Modal";
import YesNoToggle from "@components/YesNoToggle/YesNoToggle";
import CloseIcon from "@images/closeModal.svg";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UploadedFilesEditOptions.module.scss";

interface UploadedFilesEditOptionsProps {
  open: boolean;
  files: File[];
  onClose: () => void;
  onSubmit: (files: { file: File; isEditable: boolean }[]) => void;
}

const FileEditableUploadModal: React.FC<UploadedFilesEditOptionsProps> = ({
  open,
  files,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [fileStates, setFileStates] = useState<{ file: File; isEditable: boolean }[]>([]);

  useEffect(() => {
    setFileStates(files.map((file) => ({ file, isEditable: false })));
  }, [files]);

  const handleEditableChange = (index: number, value: boolean) => {
    setFileStates((prev) =>
      prev.map((f, i) => (i === index ? { ...f, isEditable: value } : f))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(fileStates);
    onClose();
  };

  return (
    <Modal modalType="primary" isOpen={open} onRequestClose={onClose}>
      <form>
        <div className={styles.header}>
          <span className={styles.title}>
            {t("collections.UploadedFilesEditOptions.title")}
          </span>
          <ButtonUpdated
            title=""
            wrapperClassNames={styles.closeButtonWrapper}
            buttonShape="circle"
            onClick={onClose}
            buttonIcon={<img alt="" className={styles.removeIcon} src={CloseIcon} />}
            hasBorder={false}
          />
        </div>
        <hr className={styles.divider} />
        <div className={styles.fileRowHeader}>
          <div className={styles.divFileName}>
            <span className={styles.headerLabel}>File Name</span>
          </div>
          <div className={styles.divToggle}>
            <span className={styles.headerLabel}>Edit Permission</span>
          </div>
        </div>
        {fileStates.map((f, idx) => (
          <div className={styles.fileRow}>
            <div className={styles.divFileName}>
              <span className={styles.fileName}>
                <b>
                  {idx + 1}
                  {". "}
                </b>
                {f.file.name}
              </span>
            </div>
            <div className={styles.divToggle}>
              <YesNoToggle
                value={f.isEditable ?? false}
                onChange={(value) => handleEditableChange(idx, value)}
              />
            </div>
          </div>
        ))}
        <div className={styles.buttonContainer}>
          <span className={styles.cancelButton} onClick={onClose} aria-label="cancel">
            {t("collections.UploadedFilesEditOptions.cancel")}
          </span>
          <Button
            type="submit"
            buttonType="primary"
            title={t("collections.UploadedFilesEditOptions.submit")}
            aria-label="button-submit"
            onClick={handleSubmit}
          />
        </div>
      </form>
    </Modal>
  );
};

export default FileEditableUploadModal;
